// ============================================================
// dom.js — Utilidades DOM: cookies, debounce
// Extraído de script.js (líneas 10-24 y 1598-1604)
// ============================================================

export function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

// ============================================================
//  CSRF — UNA SOLA FUENTE DE TOKEN (PDR-S2 · M-4)
//
//  Había cuatro llamadas del editor (`tools.js` ×2, `variations.js`, `inline-ai.js`) que mandaban
//  `getCookie('csrftoken')`. En un tenant esa cookie NO se llama así: el CSRF va por tenant y la
//  cookie es `n3i_csrf_<tenant>` (`web/middleware.py::tenant_csrf_cookie_name`), así que la
//  cabecera `X-CSRFToken` viajaba VACÍA — funcionaban solo porque sus rutas estaban exentas de
//  CSRF en `web/admin.py`. Al quitar esa exención hay que darles el token bueno.
//
//  EL ORDEN IMPORTA y es el MISMO que usa `Nube3iCsrfViewMiddleware._get_token`:
//  `csrftoken` PRIMERO y la per-tenant como respaldo. Al revés, cualquier divergencia entre las
//  dos cookies (un login rota la `csrftoken`, que es única por host) daría 403 perpetuo — el
//  comentario del middleware cuenta que ya pasó.
// ============================================================

/** Nombre de la cookie CSRF del tenant a partir de `window.BUILDER_BASE` ('/test222/builder'). */
function _nombreCookieCsrfTenant() {
    const base = (typeof window !== 'undefined' && window.BUILDER_BASE) || '';
    const trozos = String(base).split('/').filter(Boolean);
    return trozos.length ? 'n3i_csrf_' + trozos[0] : '';
}

/** Primera cookie cuyo nombre empieza por `n3i_csrf_` (respaldo si BUILDER_BASE no ayuda). */
function _cookieCsrfPorPrefijo() {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        const igual = cookie.indexOf('=');
        if (igual > 0 && cookie.substring(0, igual).indexOf('n3i_csrf_') === 0) {
            return decodeURIComponent(cookie.substring(igual + 1));
        }
    }
    return null;
}

/**
 * Token CSRF de la página. SSOT del builder: ninguna llamada lee la cookie por su cuenta.
 * Cascada: `#app-config[data-csrf]` → `<input name=csrfmiddlewaretoken>` (los dos son el token
 * que Django imprimió en ESTA página) → cookie `csrftoken` → cookie per-tenant `n3i_csrf_<t>`.
 */
export function getCsrfToken() {
    if (typeof document !== 'undefined') {
        const cfg = document.getElementById('app-config');
        const delDataset = cfg && cfg.dataset ? cfg.dataset.csrf : '';
        if (delDataset) return delDataset;
        const input = document.querySelector('[name=csrfmiddlewaretoken]');
        if (input && input.value) return input.value;
    }
    const nombreTenant = _nombreCookieCsrfTenant();
    return getCookie('csrftoken')
        || (nombreTenant ? getCookie(nombreTenant) : null)
        || _cookieCsrfPorPrefijo()
        || '';
}

export function debounce(func, timeout = 500) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => { func.apply(this, args); }, timeout);
    };
}

export function fillRangeTrack(el, color = '#6366f1', vacio = 'rgba(255,255,255,0.1)') {
    if (!el) return;
    const min = parseFloat(el.min || 0);
    const max = parseFloat(el.max || 100);
    const val = parseFloat(el.value || 0);
    const pct = ((val - min) / (max - min)) * 100;
    el.style.background = `linear-gradient(to right, ${color} ${pct}%, ${vacio} ${pct}%)`;
}

// Escapa los 5 caracteres peligrosos para interpolar texto en innerHTML/atributos del chrome del
// editor (que no pasa por el sanitizer server-side). SSOT compartida — antes había copias inline
// en varios paneles.
const _ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
/**
 * Texto de un campo de ITEM para interpolar en un template literal.
 *
 * `safe()` coalesce los textos del BLOQUE (title/subtitle/cta/logo), pero los items de un array
 * son objetos sueltos que nadie normaliza: un `{author: 'Ana'}` sin `text` hacía que
 * `${t.text}` pintara la palabra **undefined** en la tarjeta, el extractor la horneara al primer
 * guardado y se publicara. Caso real (2026-09-15, testimonios de un restaurante). Es la regla 5
 * del CLAUDE.md del front, aplicada al nivel de item.
 *
 * `== null` y no `|| ''`: en `stats` el `name` es la CIFRA, y un `0` legítimo ("0 comisiones")
 * se convertiría en cadena vacía.
 */
export function txt(v) {
    return v == null ? '' : v;
}

export function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => _ESC[c]);
}

/**
 * Serializa un objeto para un ATRIBUTO HTML. Escapa `&`, `"`, `<` y `>` (en ese orden):
 *   · `"` rompería el atributo,
 *   · `<`/`>` romperían además las regex `(<section\b[^>]*)(>)` con las que render.js y
 *     compileSiteHtml inyectan data-anim/bg-anim/spacing → el tag se cortaría a medias.
 * El navegador des-escapa al leer con getAttribute, así que el runtime hace JSON.parse directo.
 * Vive aquí (SSOT) desde T1b: lo usan `blocks/reserva-live.js` (que lo re-exporta) y
 * `blocks/menu.js` (los parámetros vivos de la carta del ERP, `data-carta-live`).
 */
export function attrJson(obj) {
    return JSON.stringify(obj == null ? {} : obj)
        .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
        .replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

